import React from "react";
import BaseFormPage from "../../base/BaseFormPage";
import { getObjectUseCase, upsertUseCase } from "../../usecases/object";
import withRouter from "../../withRouter";
import ProfilePagePresenter from "./ProfilePagePresenter";
import ProfileFieldRow from "./components/ProfileFieldRow";
import ProfileSection from "./components/ProfileSection";
import ProfileHeader from "./components/ProfileHeader";
import {
  FIELD_ICONS,
  formatRelation,
  formatDate,
  groupClientFieldsBySection,
  formatFieldValue,
} from "./components/profileUtils";
import Progress from "../../components/Progress";

class ProfilePage extends BaseFormPage {
  constructor(props) {
    super(props);
    this.presenter = new ProfilePagePresenter(
      this,
      getObjectUseCase(),
      upsertUseCase(),
    );
  }

  getCollectionName() {
    return "clients";
  }

  onEditProfile() {
    this.showUnderConstructionDialog({
      title: "Edit profile",
      message:
        "This feature is currently under construction. Please check back later.",
    });
  }

  getObjectId() {
    const { client } = this.getCurrentUser() ?? {};
    return client?.id;
  }

  componentDidMount() {
    this.presenter.componentDidMount();
  }

  render() {
    const embedded = this.props.embedded;
    const user = this.getCurrentUser() ?? {};
    const { progress, object } = this.state;
    const schema = this.getSchema(this.getCollectionName());

    if (embedded) {
      return this.renderProfileContent(user, object, schema);
    }
    if (progress) {
      return <Progress />;
    }
    return (
      <>
        {this.renderProfileContent(user, object, schema)}
        {this.renderPreviewModal()}
      </>
    );
  }

  renderProfileContent(user, client, schema) {
    const name = user?.name || user?.username || "—";
    const email = user?.email || "—";
    const hasSchema = schema && schema.fields;
    const fields = hasSchema ? schema.fields : {};
    const showRoles = "roles" in fields && user?.roles != null;
    const showBranch = "branch_access" in fields && user?.branch_access != null;
    const showActive = "isActive" in fields;
    const createdDate = user?.created ?? user?.createdAt;
    const showCreated = createdDate != null;
    const isAdmin = this.isAdmin();
    const clientSectionGroups = groupClientFieldsBySection(schema);

    const showClientSection =
      !isAdmin && client && clientSectionGroups.length > 0;

    return (
      <div className="profile-tab p-3 p-lg-3">
        <ProfileHeader
          name={name}
          email={user?.email}
          onEditProfileClick={this.onEditProfile.bind(this)}
        />

        <div className="row g-3 mb-0">
          <div className="col-lg-6">
            <ProfileSection
              title="User information"
              icon="bi bi-info-circle"
              className="mb-lg-0"
            >
              <ProfileFieldRow
                icon={FIELD_ICONS.username}
                label={fields.username?.label || "Username"}
                value={user?.username}
              />
              <ProfileFieldRow
                icon={FIELD_ICONS.email}
                label={fields.email?.label || "Email"}
                value={email}
              />
              {user?.roles != null && (
                <ProfileFieldRow
                  icon={FIELD_ICONS.name}
                  label="Role"
                  value={user.roles}
                />
              )}
            </ProfileSection>
          </div>

          <div className="col-lg-6">
            {(showRoles || showBranch || showActive) && (
              <ProfileSection
                title="Role & assignment"
                icon="bi bi-shield-check"
                className={!(showCreated || user?.id) ? "mb-lg-0" : ""}
              >
                {showRoles && (
                  <ProfileFieldRow
                    icon={FIELD_ICONS.roles}
                    label={fields.roles?.label || "Role"}
                    value={formatRelation(user.roles)}
                  />
                )}
                {showActive && (
                  <ProfileFieldRow
                    icon={FIELD_ICONS.is_active}
                    label="Status"
                    value={user.is_active}
                  >
                    <span
                      className={`badge ${
                        user.is_active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </ProfileFieldRow>
                )}
              </ProfileSection>
            )}

            {(showCreated || user?.id) && (
              <ProfileSection title="System" icon="bi bi-gear" className="mt-3">
                {showCreated && (
                  <ProfileFieldRow
                    icon={FIELD_ICONS.created}
                    label="Joined"
                    value={formatDate(createdDate)}
                  />
                )}
              </ProfileSection>
            )}
          </div>
        </div>

        <hr></hr>
        {showClientSection && (
          <div className="row g-3">
            {clientSectionGroups.map(
              ({ sectionKey, label, icon, fieldEntries }, groupIndex) => (
                <div key={sectionKey} className="col-12 col-md-6">
                  <ProfileSection
                    title={label}
                    icon={icon}
                    className={
                      groupIndex === clientSectionGroups.length - 1
                        ? "mb-0"
                        : ""
                    }
                  >
                    {fieldEntries.map(([fieldKey, fieldDef]) => (
                      <ProfileFieldRow
                        key={fieldKey}
                        label={fieldDef?.label || fieldKey}
                        value={formatFieldValue(client[fieldKey], fieldDef)}
                      />
                    ))}
                  </ProfileSection>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(ProfilePage);
